import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import ProjectForm from './ProjectForm';
import { Project, CreateProject, UpdateProject, Customer, CreateProjectPerson, ProjectTag } from '../types';
import { projectsAPI, customersAPI, projectPersonsAPI, projectTagsAPI } from '../services/api';
import { navigateWithRedirect } from '../utils/redirectUtils';

const ProjectEditPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | undefined>(undefined);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projectTags, setProjectTags] = useState<ProjectTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch customers and project tags
        const [customersData, projectTagsData] = await Promise.all([
          customersAPI.getCustomers(),
          projectTagsAPI.getProjectTags()
        ]);
        setCustomers(customersData);
        setProjectTags(projectTagsData);

        // Fetch project if editing
        if (id) {
          const projectData = await projectsAPI.getProject(parseInt(id));
          setProject(projectData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (projectData: CreateProject | UpdateProject, pendingPersons?: CreateProjectPerson[]) => {
    setIsLoading(true);
    setError(null);
    try {
      if ('id' in projectData) {
        // Update existing project
        await projectsAPI.updateProject(projectData.id, projectData);
      } else {
        // Create new project
        const createdProject = await projectsAPI.createProject(projectData);

        // If there are pending persons, save them now
        if (pendingPersons && pendingPersons.length > 0) {
          await Promise.all(
            pendingPersons.map(pp =>
              projectPersonsAPI.createProjectPerson({
                ...pp,
                projectId: createdProject.id
              })
            )
          );
        }
      }
      // Navigate back to redirect URL or default to projects list
      navigateWithRedirect(navigate, '/projects', searchParams);
    } catch (error) {
      console.error('Error saving project:', error);
      setError('Failed to save project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to redirect URL or default to projects list
    navigateWithRedirect(navigate, '/projects', searchParams);
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div className="spinner-border" role="status"></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Generate available tags from Project Tags list
  const allTags = projectTags
    .filter(tag => tag.name && tag.name.trim())
    .map(tag => ({
      id: tag.id,
      value: tag.name.trim(),
      label: tag.name.trim(),
    }));

  return (
    <div className="fade-in">
      <div className="container-fluid">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <div className="row">
          <div className="col-12">
            <ProjectForm
              project={project}
              customers={customers}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={isLoading}
              availableTags={allTags}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectEditPage;
